// ==========================================================================
// Reusable Modal Component
// ==========================================================================

/**
 * Skeleton layout configurations for loading states
 * Each layout defines how the loading skeleton should appear
 */
const skeletonLayouts = {
  // Grid layout with cards (like task view)
  grid: (options = {}) => {
    const { columns = 2, rows = 3, withSidebar = false } = options;
    const cards = Array(columns * rows).fill(0).map(() => `
      <div class="modal-skeleton__card">
        <div class="modal-skeleton__icon"></div>
        <div class="modal-skeleton__card-content">
          <div class="modal-skeleton__line modal-skeleton__line--xs"></div>
          <div class="modal-skeleton__line modal-skeleton__line--sm"></div>
        </div>
      </div>
    `).join('');

    const sidebar = withSidebar ? `
      <div class="modal-skeleton__sidebar">
        <div class="modal-skeleton__line modal-skeleton__line--full"></div>
        <div class="modal-skeleton__box"></div>
      </div>
    ` : '';

    return `
      <div class="modal-skeleton modal-skeleton--grid ${withSidebar ? 'modal-skeleton--with-sidebar' : ''}">
        <div class="modal-skeleton__main">
          <div class="modal-skeleton__cards" style="--columns: ${columns}">
            ${cards}
          </div>
        </div>
        ${sidebar}
      </div>
    `;
  },

  // List layout (like timesheet entries, comments)
  list: (options = {}) => {
    const { rows = 5 } = options;
    const items = Array(rows).fill(0).map(() => `
      <div class="modal-skeleton__list-item">
        <div class="modal-skeleton__avatar"></div>
        <div class="modal-skeleton__list-content">
          <div class="modal-skeleton__line modal-skeleton__line--md"></div>
          <div class="modal-skeleton__line modal-skeleton__line--lg"></div>
        </div>
      </div>
    `).join('');

    return `
      <div class="modal-skeleton modal-skeleton--list">
        ${items}
      </div>
    `;
  },

  // Form layout (like edit forms)
  form: (options = {}) => {
    const { fields = 4 } = options;
    const inputs = Array(fields).fill(0).map(() => `
      <div class="modal-skeleton__field">
        <div class="modal-skeleton__line modal-skeleton__line--xs"></div>
        <div class="modal-skeleton__input"></div>
      </div>
    `).join('');

    return `
      <div class="modal-skeleton modal-skeleton--form">
        ${inputs}
      </div>
    `;
  },

  // Detail view (like billing report details)
  detail: (options = {}) => {
    const { sections = 2 } = options;
    const sectionHtml = Array(sections).fill(0).map(() => `
      <div class="modal-skeleton__section">
        <div class="modal-skeleton__line modal-skeleton__line--md"></div>
        <div class="modal-skeleton__line modal-skeleton__line--full"></div>
        <div class="modal-skeleton__line modal-skeleton__line--lg"></div>
      </div>
    `).join('');

    return `
      <div class="modal-skeleton modal-skeleton--detail">
        <div class="modal-skeleton__header-block">
          <div class="modal-skeleton__line modal-skeleton__line--lg"></div>
          <div class="modal-skeleton__line modal-skeleton__line--sm"></div>
        </div>
        ${sectionHtml}
      </div>
    `;
  },

  // Simple centered spinner
  spinner: (options = {}) => {
    const { message = 'Loading...' } = options;
    return `
      <div class="modal-skeleton modal-skeleton--spinner">
        <div class="modal-skeleton__spinner-container">
          <div class="modal-skeleton__spinner"></div>
          <span class="modal-skeleton__message">${message}</span>
        </div>
      </div>
    `;
  }
};

export const modal = {
  open({ title = '', content = '', actions = [], size = 'md', onClose = null, onOpen = null, cornerAction = null, headerActions = [], headerMeta = [] } = {}) {
    // Close existing modal if any
    this.close();

    const overlay = document.createElement('div');
    overlay.className = 'modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const dialog = document.createElement('div');
    // Map size names to CSS class suffixes
    const sizeMap = {
      'small': 'sm',
      'medium': 'md',
      'large': 'lg',
      'extra-large': 'extra-large'
    };
    const sizeClass = sizeMap[size] || size;
    dialog.className = `modal__dialog modal__dialog--${sizeClass}`;
    if (size === 'extra-large') {
        overlay.classList.add('modal--extra-large');
    }

    const header = document.createElement('div');
    let headerClasses = 'modal__header';
    if (cornerAction) headerClasses += ' modal__header--with-corner-action';
    if (headerActions.length > 0 || headerMeta.length > 0) headerClasses += ' modal__header--with-actions';
    header.className = headerClasses;

    let headerContent = '';
    if (cornerAction) {
      headerContent += `
        <button class="modal__corner-action" title="${cornerAction.title || 'View Task'}" id="modal-corner-action">
          <i class="${cornerAction.icon || 'fas fa-eye'}"></i>
        </button>
      `;
    }

    headerContent += `<h3 class="modal__title">${title}</h3>`;

    // Add header meta (breadcrumbs) if provided
    if (headerMeta.length > 0) {
      headerContent += `<div class="modal__header-meta">`;
      headerMeta.forEach((item, index) => {
        if (index > 0) {
          headerContent += `<span class="modal__header-meta-divider"></span>`;
        }
        headerContent += `
          <span class="modal__header-meta-item">
            ${item.icon ? `<i class="${item.icon}"></i>` : ''}
            ${item.text}
          </span>
        `;
      });
      headerContent += `</div>`;
    }

    // Add header actions if provided
    if (headerActions.length > 0) {
      headerContent += `<div class="modal__header-actions">`;
      headerActions.forEach((action, index) => {
        headerContent += `
          <button class="modal__header-action-btn"
                  data-action="${action.action || ''}"
                  data-index="${index}"
                  title="${action.title || ''}">
            <i class="${action.icon || 'fas fa-circle'}"></i>
          </button>
        `;
      });
      headerContent += `</div>`;
    }

    headerContent += `
      <button class="modal__close" aria-label="Close">
        <i class="fas fa-times"></i>
      </button>
    `;

    header.innerHTML = headerContent;

    const body = document.createElement('div');
    body.className = 'modal__body';
    if (typeof content === 'string') {
      body.innerHTML = content;
    } else if (content instanceof HTMLElement) {
      body.appendChild(content);
    }

    const footer = document.createElement('div');
    footer.className = 'modal__footer';
    actions.forEach(({ label, kind = 'secondary', onClick, id, disabled = false }) => {
      const btn = document.createElement('button');
      btn.className = `btn btn--${kind} btn--sm`;
      btn.textContent = label;
      if (id) btn.id = id;
      if (disabled) btn.disabled = true;
      if (onClick) btn.addEventListener('click', onClick);
      footer.appendChild(btn);
    });

    dialog.appendChild(header);
    dialog.appendChild(body);
    dialog.appendChild(footer);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    // Handle corner action button
    if (cornerAction && cornerAction.onClick) {
      const cornerBtn = header.querySelector('.modal__corner-action');
      if (cornerBtn) {
        cornerBtn.addEventListener('click', cornerAction.onClick);
      }
    }

    // Handle header action buttons
    if (headerActions.length > 0) {
      header.querySelectorAll('.modal__header-action-btn').forEach((btn, index) => {
        const action = headerActions[index];
        if (action && action.onClick) {
          btn.addEventListener('click', action.onClick);
        }
      });
    }

    // Stash onClose on the overlay so programmatic close() calls can fire it
    // too (not just clicks on the X / overlay). Ensures consumers that pass
    // onClose for cleanup (e.g. EventManager dispose) are always notified.
    if (onClose) overlay.__modalOnClose = onClose;

    const closeBtn = header.querySelector('.modal__close');
    closeBtn.addEventListener('click', () => this.close());

    // Track where mousedown started to prevent closing when dragging from inside to outside
    // (e.g., when selecting text and accidentally dragging outside the modal)
    let mouseDownOnOverlay = false;
    overlay.addEventListener('mousedown', (e) => {
      mouseDownOnOverlay = e.target === overlay;
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay && mouseDownOnOverlay) this.close();
    });

    // Escape key closes the topmost modal. Stashed on the overlay so close()
    // can remove it deterministically even if multiple modals are open.
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      const openModals = document.querySelectorAll('.modal');
      if (openModals[openModals.length - 1] !== overlay) return;
      this.close();
    };
    document.addEventListener('keydown', handleEscape);
    overlay.__modalEscapeHandler = handleEscape;

    // Focus first input for accessibility and call onOpen callback
    setTimeout(() => {
      const firstInput = overlay.querySelector('input, select, textarea, button');
      if (firstInput) firstInput.focus();

      // Call onOpen callback after modal is in the DOM
      if (onOpen) onOpen();
    }, 0);

    return overlay;
  },

  showLoading(message = 'Loading...') {
    const modal = document.querySelector('.modal');
    if (!modal) return;

    const dialog = modal.querySelector('.modal__dialog');
    if (!dialog) return;

    // Check if loading overlay already exists
    let loadingOverlay = dialog.querySelector('.modal__loading-overlay');

    if (!loadingOverlay) {
      loadingOverlay = document.createElement('div');
      loadingOverlay.className = 'modal__loading-overlay';
      loadingOverlay.innerHTML = `
        <div class="modal__loading-spinner">
          <i class="fas fa-spinner"></i>
          <div class="modal__loading-text">${message}</div>
        </div>
      `;
      dialog.style.position = 'relative';
      dialog.appendChild(loadingOverlay);
    } else {
      // Update message if overlay already exists
      const textElement = loadingOverlay.querySelector('.modal__loading-text');
      if (textElement) {
        textElement.textContent = message;
      }
    }
  },

  hideLoading() {
    const loadingOverlay = document.querySelector('.modal__loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.remove();
    }
  },

  updateContent(content, title = null) {
    const modal = document.querySelector('.modal');
    if (!modal) return;

    // Update title if provided
    if (title !== null) {
      const titleElement = modal.querySelector('.modal__title');
      if (titleElement) {
        titleElement.textContent = title;
      }
    }

    // Update body content
    const body = modal.querySelector('.modal__body');
    if (body) {
      body.innerHTML = '';
      if (typeof content === 'string') {
        body.innerHTML = content;
      } else if (content instanceof HTMLElement) {
        body.appendChild(content);
      }
    }
  },

  /**
   * Opens a modal with a loading skeleton, then allows updating with real content
   *
   * @param {Object} options - Configuration options
   * @param {string} options.title - Initial title (shown during loading)
   * @param {string} options.size - Modal size ('small', 'medium', 'large', 'extra-large')
   * @param {string} options.skeleton - Skeleton layout type ('grid', 'list', 'form', 'detail', 'spinner')
   * @param {Object} options.skeletonOptions - Options for the skeleton layout
   * @param {Function} options.onClose - Callback when modal is closed
   * @returns {Object} Controller with setContent(), setError(), and close() methods
   *
   * @example
   * // Basic usage
   * const loader = modal.openWithLoading({
   *   title: 'Loading Task...',
   *   size: 'medium',
   *   skeleton: 'grid',
   *   skeletonOptions: { columns: 2, rows: 3, withSidebar: true }
   * });
   *
   * // After data loads:
   * loader.setContent({
   *   title: 'Task Name',
   *   content: myContentElement,
   *   headerMeta: [...],
   *   headerActions: [...],
   *   actions: [...]
   * });
   *
   * // On error:
   * loader.setError('Failed to load task');
   */
  openWithLoading({
    title = 'Loading...',
    size = 'medium',
    skeleton = 'spinner',
    skeletonOptions = {},
    customSkeleton = null,
    onClose = null
  } = {}) {
    // Use custom skeleton HTML if provided, otherwise generate from layout
    let skeletonHtml;
    if (customSkeleton) {
      skeletonHtml = typeof customSkeleton === 'function' ? customSkeleton() : customSkeleton;
    } else {
      const skeletonGenerator = skeletonLayouts[skeleton] || skeletonLayouts.spinner;
      skeletonHtml = skeletonGenerator(skeletonOptions);
    }

    // Open modal with skeleton
    this.open({
      title,
      content: skeletonHtml,
      size,
      onClose,
      actions: []
    });

    // Return controller object
    return {
      /**
       * Update the modal with actual content after loading completes
       */
      setContent: ({
        title: newTitle,
        content,
        headerMeta = [],
        headerActions = [],
        actions = [],
        cornerAction = null
      }) => {
        // Close loading modal and reopen with full content. Strip the stashed
        // onClose hook first so the internal close-and-reopen swap doesn't
        // fire consumer cleanup (which would wipe module state mid-flight).
        const existing = document.querySelector('.modal');
        if (existing) existing.__modalOnClose = null;
        modal.close();
        modal.open({
          title: newTitle || title,
          content,
          size,
          onClose,
          headerMeta,
          headerActions,
          actions,
          cornerAction
        });
      },

      /**
       * Show an error state in the modal
       */
      setError: (message = 'An error occurred') => {
        const errorContent = `
          <div class="modal-skeleton modal-skeleton--error">
            <div class="modal-skeleton__error-container">
              <i class="fas fa-exclamation-circle"></i>
              <span class="modal-skeleton__error-message">${message}</span>
              <button class="modal-skeleton__error-close" data-action="close-error">Close</button>
            </div>
          </div>
        `;
        this.updateContent(errorContent);
        // Attach close handler via addEventListener (no inline onclick)
        const closeBtn = document.querySelector('.modal-skeleton__error-close[data-action="close-error"]');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => modal.close());
        }
      },

      /**
       * Close the modal
       */
      close: () => {
        modal.close();
      }
    };
  },

  close() {
    const existing = document.querySelector('.modal');
    if (!existing) return;
    // Invoke any onClose hook stashed by open() before tearing the DOM
    // down. Wrapped in try/catch so a faulty hook can't leave a zombie modal.
    const onClose = existing.__modalOnClose;
    const escapeHandler = existing.__modalEscapeHandler;
    if (escapeHandler) document.removeEventListener('keydown', escapeHandler);
    existing.remove();
    if (typeof onClose === 'function') {
        try { onClose(); } catch (err) { console.error('[modal] onClose error:', err); }
    }
  },

  /**
   * Show a lightweight confirmation dialog
   * Uses a separate overlay so it can work alongside an open modal
   * @param {Object} options - Confirmation options
   * @param {string} options.title - Dialog title (default: 'Confirm')
   * @param {string} options.message - Confirmation message
   * @param {string} options.confirmText - Confirm button text (default: 'Confirm')
   * @param {string} options.cancelText - Cancel button text (default: 'Cancel')
   * @param {string} options.kind - Confirm button style: 'primary' | 'danger' (default: 'primary')
   * @returns {Promise<boolean>} Resolves true if confirmed, false if cancelled
   *
   * @example
   * const confirmed = await modal.confirm({
   *   title: 'Delete Item',
   *   message: 'Are you sure you want to delete this item?',
   *   confirmText: 'Delete',
   *   kind: 'danger'
   * });
   * if (confirmed) { ... }
   */
  confirm({
    title = 'Confirm',
    message = 'Are you sure?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    kind = 'primary'
  } = {}) {
    return this.choose({
      title,
      message,
      choices: [
        { label: cancelText, value: false, kind: 'secondary' },
        { label: confirmText, value: true, kind, focus: true }
      ],
      dismissValue: false
    });
  },

  /**
   * Show a lightweight multi-choice dialog (confirm-style chrome) with N buttons.
   * Used when a boolean confirm is not enough (e.g. "Save / Save As / Cancel").
   *
   * @param {Object} options
   * @param {string} [options.title='Choose']
   * @param {string} [options.message=''] - HTML allowed (interpolated into <p>)
   * @param {Array} options.choices - [{ label, value, kind?, focus? }]
   *   - label: button text
   *   - value: what the Promise resolves to when clicked
   *   - kind: btn modifier ('primary' | 'danger' | 'secondary', default 'secondary')
   *   - focus: if true, autofocus this button
   * @param {*} [options.dismissValue=null] - Value resolved on Escape/backdrop click
   * @returns {Promise<*>} Resolves to the chosen value or dismissValue
   *
   * @example
   * const choice = await modal.choose({
   *   title: 'Unsaved Changes',
   *   message: 'You have unsaved changes.',
   *   choices: [
   *     { label: 'Cancel',   value: 'cancel' },
   *     { label: 'Discard',  value: 'discard', kind: 'danger' },
   *     { label: 'Save',     value: 'save',    kind: 'primary', focus: true }
   *   ],
   *   dismissValue: 'cancel'
   * });
   */
  choose({
    title = 'Choose',
    message = '',
    choices = [],
    dismissValue = null
  } = {}) {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'modal-confirm';
      overlay.setAttribute('role', 'alertdialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', title);

      const dialog = document.createElement('div');
      dialog.className = 'modal-confirm__dialog';

      const buttonsHtml = choices.map((c, i) => {
        const kind = c.kind || 'secondary';
        return `<button class="btn btn--${kind} btn--sm" data-choice-index="${i}">${c.label}</button>`;
      }).join('');

      dialog.innerHTML = `
        <div class="modal-confirm__header">
          <h4 class="modal-confirm__title">${title}</h4>
        </div>
        <div class="modal-confirm__body">
          <p class="modal-confirm__message">${message}</p>
        </div>
        <div class="modal-confirm__footer">
          ${buttonsHtml}
        </div>
      `;

      overlay.appendChild(dialog);
      document.body.appendChild(overlay);

      const handleKeydown = (e) => {
        if (e.key === 'Escape') cleanup(dismissValue);
      };

      const cleanup = (result) => {
        document.removeEventListener('keydown', handleKeydown);
        overlay.remove();
        resolve(result);
      };

      dialog.querySelectorAll('[data-choice-index]').forEach((btn) => {
        const idx = Number(btn.dataset.choiceIndex);
        btn.addEventListener('click', () => cleanup(choices[idx]?.value));
      });

      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) cleanup(dismissValue);
      });

      document.addEventListener('keydown', handleKeydown);

      // Focus the explicitly-marked button, or fall back to the last one
      // (which matches the previous confirm-button behaviour).
      const focusIndex = choices.findIndex(c => c.focus);
      const target = dialog.querySelector(
        `[data-choice-index="${focusIndex >= 0 ? focusIndex : choices.length - 1}"]`
      );
      if (target) setTimeout(() => target.focus(), 0);
    });
  }
};

